import React, { useEffect, useState } from "react";
import { RefreshCw, Inbox, Loader2, Save, Search, Send, Trash2, MailCheck, MailX } from "lucide-react";
import { fetchOrders, updateOrder, fetchSettings, fetchEmailTemplates, emailOrder, deleteOrder } from "../../lib/api";
import { toast } from "../../hooks/use-toast";

const STAGES = ["Order Placed", "Payment Confirmed", "Processing", "Shipped", "In Transit", "Out for Delivery", "Delivered"];
const ORDER_STATUS = ["pending", "paid", "shipped", "delivered", "cancelled"];
const statusColor = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-400/30",
  paid: "bg-blue-500/15 text-blue-300 border-blue-400/30",
  shipped: "bg-indigo-500/15 text-indigo-300 border-indigo-400/30",
  delivered: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-400/30",
};
const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";

const OrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    fetchOrders().then(setOrders).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    fetchSettings().then((s) => setCarriers(s.carriers || [])).catch(() => {});
    fetchEmailTemplates().then(setTemplates).catch(() => {});
  }, []);

  const sendEmail = async (o) => {
    const t = tpl[o.id] || "confirmation";
    setBusy(`mail-${o.id}`);
    try { await emailOrder(o.id, t); toast({ title: "Email sent", description: `Sent to ${o.customer?.email}` }); load(); }
    catch (e) { toast({ title: "Email failed", description: e?.response?.data?.detail || "Could not send email." }); load(); }
    finally { setBusy(""); }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Delete order ${o.orderId}? This cannot be undone.`)) return;
    await deleteOrder(o.id); toast({ title: "Deleted", description: `Order ${o.orderId} removed.` }); load();
  };

  const term = q.trim().toLowerCase();
  const shown = orders.filter((o) => (filter === "all" || o.status === filter) &&
    (!term || `${o.orderId} ${o.customer?.name} ${o.customer?.email}`.toLowerCase().includes(term)));

  const setField = (id, k, v) => setOrders((o) => o.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  const saveOrder = async (o) => {
    setBusy(o.id);
    try {
      await updateOrder(o.id, {
        status: o.status,
        carrier: o.carrier || "",
        trackingNumber: o.trackingNumber || "",
        trackingStage: o.trackingStage || "Order Placed",
        historyNote: notes[o.id] || undefined,
      });
      setNotes((n) => ({ ...n, [o.id]: "" }));
      toast({ title: "Updated", description: `Order ${o.orderId} saved.` });
      load();
    } catch { toast({ title: "Error", description: "Could not update order." }); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-white font-bold text-lg">Orders ({shown.length})</h3>
        <button onClick={load} data-testid="orders-refresh-btn" className="inline-flex items-center gap-2 text-sm text-white border border-gold/30 hover:border-gold px-3 py-2 rounded-lg transition-colors"><RefreshCw size={15} /> Refresh</button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" /><input data-testid="orders-search-input" className={`${input} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by order ID, name or email" /></div>
        <select data-testid="orders-status-filter" className={`${input} sm:w-48`} value={filter} onChange={(e) => setFilter(e.target.value)}><option value="all">All statuses</option>{ORDER_STATUS.map((st) => <option key={st} value={st}>{st}</option>)}</select>
      </div>

      {loading ? <p className="text-slate-400">Loading orders...</p> : shown.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><Inbox size={40} className="mx-auto text-navy-600 mb-4" /><p>No orders yet.</p></div>
      ) : (
        <div className="space-y-5">
          {shown.map((o) => (
            <div key={o.id} className="bg-navy-800 rounded-2xl border border-white/10 p-5" data-testid={`order-card-${o.orderId}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div><span className="text-gold font-semibold">{o.orderId}</span><span className="text-slate-500 text-xs ml-3">{o.createdAt ? new Date(o.createdAt).toLocaleString() : ""}</span></div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-3 py-1 rounded-full border ${statusColor[o.status] || ""}`}>{o.status}</span>
                  <button onClick={() => removeOrder(o)} data-testid={`order-delete-${o.orderId}`} className="h-8 w-8 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 text-sm mb-5">
                <div>
                  <p className="text-slate-400 mb-1">Customer</p>
                  <p className="text-white">{o.customer?.name} · {o.customer?.email}</p>
                  <p className="text-slate-300">{o.customer?.address}, {o.customer?.city} {o.customer?.zip}, {o.customer?.country}</p>
                  <p className="text-slate-400 mt-2">Payment: <span className="text-white">{o.paymentMethod}</span></p>
                  {o.shippingMethod && <p className="text-slate-400">Shipping: <span className="text-white">{o.shippingMethod.name} (${Number(o.shippingMethod.price).toFixed(2)})</span></p>}
                </div>
                <div>
                  <p className="text-slate-400 mb-1">Items</p>
                  {(o.items || []).map((it, i) => <p key={i} className="text-slate-300">{it.qty} × {it.name} ({it.option})</p>)}
                  {o.discount > 0 && <p className="text-emerald-400 mt-2">Promo {o.promoCode}: -${Number(o.discount).toFixed(2)}</p>}
                  <p className="text-gold font-semibold mt-2">Total: ${Number(o.total).toFixed(2)}</p>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <p className="text-white font-semibold mb-3">Shipment &amp; Tracking</p>
                <div className="grid sm:grid-cols-4 gap-3">
                  <div><label className={label}>Order Status</label><select data-testid={`order-status-select-${o.orderId}`} className={input} value={o.status} onChange={(e) => setField(o.id, "status", e.target.value)}>{ORDER_STATUS.map((st) => <option key={st}>{st}</option>)}</select></div>
                  <div><label className={label}>Carrier</label><select className={input} value={o.carrier || ""} onChange={(e) => setField(o.id, "carrier", e.target.value)}><option value="">— none —</option>{carriers.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
                  <div><label className={label}>Tracking #</label><input className={input} value={o.trackingNumber || ""} onChange={(e) => setField(o.id, "trackingNumber", e.target.value)} /></div>
                  <div><label className={label}>Tracking Stage</label><select data-testid={`order-stage-select-${o.orderId}`} className={input} value={o.trackingStage || "Order Placed"} onChange={(e) => setField(o.id, "trackingStage", e.target.value)}>{STAGES.map((st) => <option key={st}>{st}</option>)}</select></div>
                </div>
                <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1"><label className={label}>Add update note (optional)</label><input className={input} value={notes[o.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [o.id]: e.target.value }))} placeholder="e.g. Package handed to USPS" /></div>
                  <button onClick={() => saveOrder(o)} disabled={busy === o.id} data-testid={`order-update-btn-${o.orderId}`} className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-60">{busy === o.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Update</button>
                </div>
                <div className="mt-5 border-t border-white/10 pt-4">
                  <p className="text-white font-semibold mb-3">Email Customer</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <select data-testid={`order-email-template-${o.orderId}`} className={`${input} sm:w-64`} value={tpl[o.id] || "confirmation"} onChange={(e) => setTpl((t) => ({ ...t, [o.id]: e.target.value }))}>
                      {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                    <button onClick={() => sendEmail(o)} disabled={busy === `mail-${o.id}`} data-testid={`order-email-send-${o.orderId}`} className="inline-flex items-center justify-center gap-2 border border-gold/40 text-gold hover:bg-gold/10 font-semibold px-5 py-2 rounded-xl transition-colors disabled:opacity-60">{busy === `mail-${o.id}` ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send to {o.customer?.email}</button>
                  </div>
                  {o.emailLog?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {o.emailLog.slice(-6).reverse().map((e, i) => (
                        <span key={i} title={e.error || ""} className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${e.ok ? "border-emerald-400/30 text-emerald-300" : "border-red-400/30 text-red-300"}`}>{e.ok ? <MailCheck size={12} /> : <MailX size={12} />} {e.label} · {new Date(e.at).toLocaleDateString()}</span>
                      ))}
                    </div>
                  )}
                </div>
                {o.trackingHistory?.length > 0 && (
                  <div className="mt-4 space-y-1.5">
                    {[...o.trackingHistory].reverse().map((h, i) => (
                      <p key={i} className="text-xs text-slate-400"><span className="text-gold">{h.stage}</span> — {h.note} <span className="text-slate-600">({h.at ? new Date(h.at).toLocaleString() : ""})</span></p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPanel;
